import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Lock, Loader2 } from 'lucide-react';
import { getWeaveinApiUrl, config } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { CONSENT_VERSION } from '@/constants/legalTexts';

interface ConsentDocument {
  id: number;
  document_type: string;
  version: string;
  title: string;
  content: string;
  effective_date: string;
  is_active: boolean;
  summary?: string;
}

export default function PrivacyPolicyPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [document, setDocument] = useState<ConsentDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [useApiContent, setUseApiContent] = useState(false);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/member/home');
    }
  };

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(
          `${getWeaveinApiUrl()}/api/consent/documents/privacy_policy${config.isTriveny ? '?product=triveny' : ''}`,
        );
        if (response.ok) {
          const data = await response.json();
          setDocument(data);
          setUseApiContent(data.content && data.content.length > 1000);
        }
      } catch (error) {
        console.error('Failed to fetch privacy policy document, using fallback:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, []);

  const effectiveDate = document?.effective_date
    ? new Date(document.effective_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'April 1, 2021';

  const version = document?.version || CONSENT_VERSION;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" className="text-teal-700 hover:text-teal-800" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('privacy-policy.back', 'Back')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                <Lock className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <CardTitle className="text-2xl">{t('privacy-policy.title', 'Privacy Policy')}</CardTitle>
                <p className="text-sm text-gray-600">
                  Version {version} • Last updated: {effectiveDate}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : useApiContent && document?.content ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {document.content}
              </pre>
            ) : (
              <>
                <h2>1. Introduction</h2>
                <p>
                  Harmony Healthcare Solutions, Inc. ("Harmony Health," "we," "us," or "our") respects the privacy of its users ("User," "your," or "you"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Harmony Health Platform ("Platform") through our website at https://weavein.ai/ ("Website") or our mobile application ("App").
                </p>
                <p>
                  PLEASE READ THIS PRIVACY POLICY CAREFULLY. BY ACCESSING OR USING OUR WEBSITE, APP, AND PLATFORM, YOU AGREE TO ACCEPT ALL THE TERMS CONTAINED IN THIS PRIVACY POLICY. IF YOU DO NOT AGREE, PLEASE DO NOT ACCESS OR USE OUR WEBSITE, APP, OR PLATFORM.
                </p>
                <p><strong>WE DO NOT SELL YOUR PERSONAL INFORMATION, NOR DO WE INTEND TO DO SO.</strong></p>

                <h2>2. Territorial Restriction</h2>
                <p>
                  Our Website, App, and Platform are only available for use outside the European Union. If you are a resident of, or visitor to, the European Union, please do not download, register, or use our Website, App, or Platform. If you are a resident of the United States, the laws of the State of Delaware shall apply. If you are a resident of any other country, please ensure compliance with all local laws prior to using our services.
                </p>

                <h2>3. What Information Do We Collect?</h2>
                <p>
                  When you register to use our Website, App, or Platform, we collect personal information (also referred to as personally identifiable information or PII) which may include:
                </p>
                <ul>
                  <li>Your name and address</li>
                  <li>Email address and username</li>
                  <li>Phone number</li>
                  <li>Health-related data necessary for care coordination</li>
                  <li>Other personal information you provide</li>
                </ul>
                <p>
                  <strong>Geolocation and Equipment Information.</strong> We may also collect information that does not personally identify you, such as your geolocation and information about your internet connection, the equipment you use to access our services, and usage details.
                </p>

                <h2>4. How Do We Collect Information?</h2>
                <p>We collect personal information from you in the following ways:</p>
                <ul>
                  <li>At registration on our Website, App, or Platform</li>
                  <li>In email, text, and other electronic messages between you and our services</li>
                  <li>Through mobile and desktop applications you download from us</li>
                  <li>When you interact with our applications on third-party websites and services</li>
                  <li>From forms filled out by you</li>
                  <li>From records or copies of correspondences if you contact us</li>
                  <li>When you post information to be published or displayed on our Platform</li>
                </ul>
                <p>We also collect information automatically when you navigate through our services:</p>
                <ul>
                  <li>Usage details</li>
                  <li>IP addresses</li>
                  <li>Information obtained through browser cookies</li>
                  <li>Other tracking technologies</li>
                </ul>

                <h2>5. How Do We Use Your Information?</h2>
                <p>We use the information that you provide to:</p>
                <ul>
                  <li>Provide and improve our services</li>
                  <li>Coordinate your healthcare and community resources</li>
                  <li>Communicate with you about your care</li>
                  <li>Personalize your experience using our Platform</li>
                  <li>Provide you with notices about your account</li>
                  <li>Carry out obligations and enforce rights arising from contracts between you and us</li>
                  <li>Notify you about changes to our services</li>
                  <li>Allow you to participate in interactive features on our Platform</li>
                  <li>Process transactions</li>
                  <li>Anonymize and aggregate data for statistics</li>
                  <li>Comply with legal obligations</li>
                </ul>

                <h2>6. Our Cookie Policy</h2>
                <p>
                  Cookies are small pieces of text used to store information on web browsers. We use cookies on our Website and App to understand and save your preferences for future visits, and to compile aggregate data about site traffic and interactions. You can set your browser to refuse all or some browser cookies, but it may affect your user experience. We honor Do Not Track signals and, if one is in place, we will not track or plant cookies.
                </p>

                <h2>7. How Do We Protect Information We Collect?</h2>
                <p>
                  Our Website and App are regularly scanned to meet or exceed PCI Compliance. We perform regular security scans, penetration tests, and malware scans. Our services use SSL certificates as an added security measure. We require username and passwords for employees who can access your personal information that we store and process. We actively prevent third parties from accessing your personal information.
                </p>

                <h2>8. Data Security Measures</h2>
                <p>
                  <strong>Security Measures.</strong> We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure. All information you provide to us is stored on our secure servers behind firewalls.
                </p>
                <p>
                  <strong>Fair Information Practice Principles.</strong> In the event of a personal data breach, we will notify you within fifteen (15) days via email and/or our Platform notification system. We agree to the individual redress principle, which requires that individuals have a right to pursue legally enforceable rights against data collectors and processors who fail to adhere to the law.
                </p>

                <h2>9. Information Sharing</h2>
                <p>We share information only with:</p>
                <ul>
                  <li>Healthcare providers involved in your care</li>
                  <li>Community resource providers you are referred to</li>
                  <li>Contractors, service providers, and third-party partners under contract</li>
                  <li>As required by law</li>
                </ul>
                <p>We will ensure that our partners protect your personal information.</p>
                <p>
                  <strong>Text messaging (SMS) opt-in and consent are excluded from all sharing described above.</strong> We do not share or sell your mobile phone number, or your SMS/text-messaging opt-in and consent, to third parties or affiliates for marketing or promotional purposes. See Section 10.
                </p>

                <h2>10. SMS/Text Messaging Program and Consent</h2>
                <p>
                  <strong>Consent to receive texts.</strong> When you provide your mobile phone number and opt in — during enrollment, on a form, or when you or your care organization request it — you consent to receive SMS text messages from Harmony Health / WeaveIn about your care and services. Consenting to receive text messages is not a condition of receiving services.
                </p>
                <p>
                  <strong>Types of messages.</strong> Messages may include care-coordination and case-management updates, appointment and program reminders, benefit and eligibility notifications, surveys and feedback requests, and other service-related communications. We do not send marketing or promotional text messages without your separate express consent.
                </p>
                <p>
                  <strong>Message frequency and rates.</strong> Message frequency varies based on your care activity and interactions. Message and data rates may apply.
                </p>
                <p>
                  <strong>Opt-out and help.</strong> You can opt out at any time by replying <strong>STOP</strong> to any text message; you will receive a confirmation and will not receive further messages unless you opt back in. Reply <strong>HELP</strong> for help, or contact us at <a href="mailto:support@weavein.ai" className="text-teal-600">support@weavein.ai</a>.
                </p>
                <p>
                  <strong>Mobile information is never shared or sold for marketing.</strong> No mobile information — including your phone number and your SMS/text-messaging opt-in and consent — will be shared with or sold to third parties or affiliates for marketing or promotional purposes. All of the data-sharing categories described in this Privacy Policy exclude text-messaging originator opt-in data and consent; this information will not be shared with any third parties, and is used solely to deliver the messages you have requested (including through our messaging service providers acting on our behalf under contract).
                </p>

                <h2>11. Your Rights</h2>
                <p>You have the right to:</p>
                <ul>
                  <li>Access your personal information</li>
                  <li>Request corrections to your information</li>
                  <li>Request deletion of your information (subject to legal requirements)</li>
                  <li>Opt-out of certain communications</li>
                </ul>
                <p>
                  Upon your written request, we will provide you with a list of all personal information that we store on you within sixty (60) days of receiving your request. Upon your prior written request, we will delete any such information within sixty (60) days.
                </p>

                <h2>12. California Privacy Rights</h2>
                <p>
                  We do not sell your personal information. California residents have additional rights under the California Civil Code, including the right to request information about the categories and specific pieces of personal information we have collected, the categories of sources from which the information is collected, and the business purpose for collecting the information.
                </p>

                <h2>13. Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul>
                  <li>Email: <a href="mailto:support@weavein.ai" className="text-teal-600">support@weavein.ai</a></li>
                  <li>Mail: Harmony Healthcare Solutions, Inc., 5776 Stoneridge Mall Road, Suite 125, Pleasanton, CA 94544</li>
                </ul>

                <div className="bg-teal-50 border-l-4 border-teal-600 p-4 mt-8">
                  <p className="font-semibold text-teal-900">{t('privacy-policy.your_privacy_matters', 'Your Privacy Matters')}</p>
                  <p className="text-sm text-teal-800 mt-2">
                    By using the Platform, you acknowledge that you have read and understood this Privacy Policy and agree to the collection and use of your information as described herein.
                  </p>
                </div>

                <div className="text-center mt-8 pt-6 border-t">
                  <p className="text-sm text-gray-600">
                    &copy; {currentYear} Harmony Healthcare Solutions, Inc. All rights reserved.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    HARMONY HEALTH DOES NOT PROVIDE MEDICAL SERVICES. IF YOU REQUIRE MEDICAL ATTENTION, PLEASE CALL YOUR REGULAR DOCTOR.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
